import * as React from "react";
import { motion, AnimatePresence, HTMLMotionProps } from "framer-motion";
import styled from "styled-components";
import { Typography } from "@shared/ui/Typography";
import {
  AccordionCollapsedIcon,
  AccordionOpenedIcon,
} from "public/static/icons";

interface IProps extends HTMLMotionProps<"div"> {
  expandedId: number;
  expanded: false | number;
  setExpanded: (id: false | number) => void;
  question: string;
  answer: string;
  className?: string;
}

export function Accordion({
  expandedId,
  expanded,
  setExpanded,
  question,
  answer,
  className,
  ...motionProps
}: IProps) {
  const isOpen = expandedId === expanded;

  return (
    <StyledAccordion {...motionProps} className={className}>
      <StyledHeader
        initial={false}
        onClick={() => setExpanded(isOpen ? false : expandedId)}
      >
        <Typography weight={500} fontSize="1.8rem">
          {question}
        </Typography>
        <div>
          {isOpen ? <AccordionOpenedIcon /> : <AccordionCollapsedIcon />}
        </div>
      </StyledHeader>
      <AnimatePresence initial={false}>
        {isOpen && (
          <StyledContent
            key="content"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { height: "auto", opacity: 1 },
              collapsed: { height: 0, opacity: 0 },
            }}
            transition={{ duration: 0.6, ease: [0.04, 0.62, 0.23, 0.98] }}
          >
            <StyledWrapper>
              <Typography
                tag="div"
                dangerouslySetInnerHTML={{ __html: answer }}
              />
            </StyledWrapper>
          </StyledContent>
        )}
      </AnimatePresence>
    </StyledAccordion>
  );
}

const StyledAccordion = styled(motion.div)`
  background: ${(props) => props.theme.colors.primary_light_gray}26;
  box-shadow: ${(props) => props.theme.shadow["30dp"]};
  border-radius: 0.4rem;
`;

const StyledHeader = styled(motion.header)`
  padding: 2.4rem;
  display: grid;
  grid-template-columns: 1fr auto;
  grid-gap: 1.6rem;
  cursor: pointer;
`;

const StyledContent = styled(motion.section)`
  overflow: hidden;
`;

const StyledWrapper = styled(motion.div)`
  padding: 0 2.4rem 2.4rem 2.4rem;
  max-width: 77.5rem;
`;
